import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import { Upload } from '@/components/upload';
import { nameValidationRegex } from '@/constants/regex';
import { Label } from '@/design-system/label';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { uploadValueSchema } from '@/lib/uploadSchema';
import { GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { useAddStoreGallery } from '../hooks/useAddStoreGallery';
import { useUpdateStoreGallery } from '../hooks/useUpdateStoreGallery';

const addHostGalleryFormSchema = z.object({
  imageTitle: z.string().min(1, { message: 'common.form.validation.required' }).regex(nameValidationRegex, {
    message: 'common.form.validation.nameOnly',
  }),
  imagePath: uploadValueSchema,
});

export const AddGalleryImageForm = ({
  clubId,
  hostId,
  data,
  type,
}: {
  clubId: string;
  hostId: string;
  data?: GalleryManagement;
  type?: 'host' | 'store';
}) => {
  const form = useForm({
    schema: addHostGalleryFormSchema,
    mode: 'all',
    defaultValues: {
      imageTitle: data?.gallery.description ?? '',
      imagePath: data?.gallery.path ?? '',
    },
  });

  const [imagePreview, setImagePreview] = useState(data?.gallery.path ?? '');
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const { t } = useDynamicLocale();
  const { mutateAsync } = useAddStoreGallery();
  const { mutateAsync: updateGallery } = useUpdateStoreGallery();

  useEffect(() => {
    return () => {
      if (cropSource?.startsWith('blob:')) {
        URL.revokeObjectURL(cropSource);
      }
    };
  }, [cropSource]);

  return (
    <>
      <Form
        form={form}
        onSubmit={async () => {
          const imageFile = form.getValues('imagePath');
          const doesImageExist = typeof imageFile === 'string';

          const imageUrl = !doesImageExist
            ? await uploadFileToS3WithPresignedUrl((imageFile as FileObject) || undefined)
            : imageFile;

          if (data) {
            await updateGallery({
              galleryId: data.galleryId,
              clubId: clubId || '',
              hostId: hostId || '',
              imagePath: imageUrl || '',
              imageTitle: form.getValues('imageTitle'),
            });
          } else {
            await mutateAsync({
              clubId: clubId || '',
              hostId: hostId || '',
              imagePath: imageUrl || '',
              imageTitle: form.getValues('imageTitle'),
            });
          }
        }}
      >
        <div className="w-full flex flex-col gap-4.5">
          <Input
            label="storeManagement.galleryManagement.page.addGallery.title"
            maxLength={50}
            {...form.register('imageTitle')}
            placeholder={t('storeManagement.galleryManagement.page.addGallery.titlePlaceholder')}
          />

          <div>
            <Label className="text-body font-bold leading-normal">
              <Translation id="storeManagement.galleryManagement.page.addGallery.uploadImage" />
              <span className="text-destructive-text leading-[100%]">*</span>
            </Label>

            <Controller
              name="imagePath"
              control={form.control}
              render={({ field, fieldState }) => (
                <Upload
                  maxSizeMB={5}
                  accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                  dropZoneClassName={`${type === 'host' ? 'h-[380px]' : 'h-[267px]'} w-full bg-surface-subtle`}
                  key={uploadKey}
                  className="mt-2"
                  multiple={false}
                  error={fieldState.error?.message}
                  defaultPreviewUrl={imagePreview}
                  previewClassName={`w-full ${type === 'host' ? 'object-contain h-[380px]' : 'object-cover h-[267px] '}  `}
                  onFilesChange={(files) => {
                    const selected = files?.[0];

                    if (!selected || typeof selected === 'string') {
                      field.onChange('');
                      return;
                    }

                    const rawFile = selected.file as File;
                    const localPreview = selected.preview || URL.createObjectURL(rawFile);
                    setCropSource(localPreview);
                    setIsCropOpen(true);
                  }}
                  onDefaultPreviewRemove={() => {
                    if (imagePreview) {
                      setImagePreview('');
                    }
                    form.setValue('imagePath', '', { shouldValidate: true });
                  }}
                />
              )}
            />

            <div className="mt-2 text-muted-foreground text-caption font-medium">
              {t('common.form.placeholder.imageSizeWarning')}
            </div>
          </div>
        </div>
      </Form>

      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        type={type}
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }
          setCropSource(null);
          setUploadKey((k) => k + 1);
          // Restore to previous valid state instead of blanking out
          if (!data?.gallery.path) {
            setImagePreview('');
          }
          setImagePreview(data?.gallery.path ?? '');
          form.setValue('imagePath', data?.gallery.path ?? '', { shouldValidate: true });
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: croppedFile,
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          // Revoke the temporary crop source blob before clearing it
          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setImagePreview(previewUrl);
          form.setValue('imagePath', croppedFileObject, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </>
  );
};
